import { useParams } from "react-router-dom";
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import Masonry from "@mui/lab/Masonry";
import { MAIN_FONT_FAMILY } from "../../../../../../data/constant/Constants";
import {
  COLOR_BLACK,
  COLOR_GOLD,
  COLOR_GREEN,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_RED,
  COLOR_WHITE,
  GRADIENT_MAIN,
} from "../../../../../../data/colors/Colors";
import {
  Attachment,
  AudioFileOutlined,
  AudioFileRounded,
  CategoryRounded,
  CheckCircle,
  CloudUploadRounded,
  Delete,
  DeleteRounded,
  EditRounded,
  ImageOutlined,
  Preview,
  PreviewRounded,
  Save,
  StarRateRounded,
  VideoFileOutlined,
} from "@mui/icons-material";
import { CustomImageView } from "../../../../../component/CustomImageView";
import useWindowDimensions from "../../../../../../core/utils/UseWindowDimensions";
import {
  STRINGS_ANSWER,
  STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_QUESTION,
  STRINGS_ATTACHMENT,
  STRINGS_CATEGORY,
  STRINGS_CREATE,
  STRINGS_DELETE,
  STRINGS_DELETE_QUESTION,
  STRINGS_DIFFICULTY,
  STRINGS_EDIT_TITLE,
  STRINGS_EDIT_VALUE,
  STRINGS_PREVIEW,
  STRINGS_QUESTION,
  STRINGS_RATINGS,
  STRINGS_SAVE,
  STRINGS_SUBCATEGORY,
  STRINGS_UNCERTAINTY,
} from "../../../../../../data/strings/Strings";
import { useEffect, useMemo, useRef, useState } from "react";
import AutoHeight from "../../../../../component/AutoHeightView";
import { Difficulty } from "../../../../../../domain/model/Question";
import { TitleView } from "../../../../../component/TitleView";
import { CustomTextView } from "../../../../../component/CustomTextView";
import { CustomTextField } from "../../../../../component/CustomTextField";
import { HeaderView } from "../../../../../component/HeaderView";
import { CustomTitledView } from "../../../../../component/CustomTitledView";
import { FilePicker } from "../../../../../component/FilePicker";
import { QuestionDetailsViewModel } from "./QuestionDetailsViewModel";
import { ROUTE_NEW } from "../../../../../../data/constant/RouteConstants";
import { CustomPopoverView } from "../../../../../component/CustomPopoverView";
import ReactPlayer from "react-player";
import ReactAudioPlayer from "react-audio-player";
import {
  AttachmentType,
  Attachment as AttachmentModel,
} from "../../../../../../domain/model/Attachment";
import {
  provideCreateAnswerUseCase,
  provideCreateQuestionUseCase,
  provideDeleteAnswerAttachmentUseCase,
  provideDeleteAnswerUseCase,
  provideDeleteQuestionAttachmentUseCase,
  provideDeleteQuestionUseCase,
  provideGetAllCategoriesUseCase,
  provideGetAllQuestionsUseCase,
  provideGetAnswerByQuestionIdUseCase,
  provideGetQuestionUseCase,
  provideUpdateAnswerUseCase,
  provideUpdateQuestionUseCase,
  provideUploadFileUseCase,
} from "../../../../../di/UseCasesModule";
import { LoadingView } from "../../../../../component/LoadingView";
import { CustomAlertDialog } from "../../../../../component/CustomAlertDialog";
import { CustomCardView } from "../../../../../component/CustomCardView";
import {
  convertMillisecondsToSecondsString,
  formateDate,
  hasEnglishLetters,
} from "../../../../../../core/utils/Utils";
import { Gauge, gaugeClasses } from "@mui/x-charts";
import { CustomSwitchView } from "../../../../../component/CustomSwitchView";
import Marquee from "react-fast-marquee";

export function QuestionDetailsView() {
  const { id } = useParams();
  const viewModel = QuestionDetailsViewModel(
    provideGetQuestionUseCase(),
    provideGetAllQuestionsUseCase(),
    provideGetAllCategoriesUseCase(),
    provideGetAnswerByQuestionIdUseCase(),
    provideCreateQuestionUseCase(),
    provideUpdateQuestionUseCase(),
    provideDeleteQuestionUseCase(),
    provideDeleteQuestionAttachmentUseCase(),
    provideCreateAnswerUseCase(),
    provideUpdateAnswerUseCase(),
    provideDeleteAnswerUseCase(),
    provideDeleteAnswerAttachmentUseCase(),
    provideUploadFileUseCase(),
  );
  const isNewPage = id === ROUTE_NEW;
  const { height } = useWindowDimensions();

  const [questionTitleEditable, setQuestionTitleEditable] = useState(isNewPage);
  const [answerTitleEditable, setAnswerTitleEditable] = useState(isNewPage);
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [showDeleteAttachmentAlertDialog, setShowDeleteAttachmentAlertDialog] =
    useState(false);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    isQuestion: boolean,
  ) => {
    setAnchorEl(event.currentTarget);
    viewModel.setAttachmentBeingUsedFor(isQuestion ? "question" : "answer");
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { width } = useWindowDimensions();

  useEffect(() => {
    if (!isNewPage && id) {
      viewModel.fetchQuestion(id);
    } else {
      viewModel.fetchCategories();
    }
  }, []);

  const [rightColumnHeight, setRightColumnHeight] = useState();
  const ref = useRef(null);

  const [answerStatsRefHeight, setAnswerStatsRefHeight] = useState();
  const answerStatsRef = useRef(null);

  useEffect(() => {
    if (ref.current) {
      setRightColumnHeight((ref.current as any).clientHeight);
    }

    if (answerStatsRef.current) {
      setAnswerStatsRefHeight((answerStatsRef.current as any).clientHeight);
    }
  });

  return (
    <Box>
      <CustomAlertDialog
        title={STRINGS_DELETE_QUESTION}
        description={STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_QUESTION}
        open={showDeleteAlertDialog}
        handleNegativeButtonClick={() => setShowDeleteAlertDialog(false)}
        handlePositiveButtonClick={() => {
          viewModel.deleteQuestion();
          setShowDeleteAlertDialog(false);
        }}
      />
      <CustomAlertDialog
        title={"Delete Attachment"}
        description={"Are you sure you want to delete this attachment?"}
        open={showDeleteAttachmentAlertDialog}
        handleNegativeButtonClick={() =>
          setShowDeleteAttachmentAlertDialog(false)
        }
        handlePositiveButtonClick={() => {
          setShowDeleteAttachmentAlertDialog(false);
          viewModel.deleteQuestionOrAnswerAttachment();
        }}
      />
      <Backdrop
        sx={{ color: COLOR_WHITE, zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={viewModel.isBackdropLoading}
      >
        <Stack spacing={2} alignItems={"center"}>
          <CircularProgress color="inherit" />
          <CustomTextView
            text={viewModel.backdropLoadingStatus}
            fontWeight="bold"
            textColor={COLOR_WHITE}
            variant="h6"
          />
        </Stack>
      </Backdrop>
      <CustomPopoverView
        layout={
          <Stack>
            <Button>
              <FilePicker
                handleFilePicked={(file) => {
                  console.log(file);
                  if (!file) return;
                  viewModel.setQuestionOrAnswerAttachment(
                    file,
                    AttachmentType.IMAGE,
                  );
                  handleClose();
                }}
                layout={
                  <Stack direction={"row"} p={2} spacing={2}>
                    <ImageOutlined sx={{ color: COLOR_LESS_BLACK }} />
                    <CustomTextView
                      text="Image"
                      props={{ textTransform: "none" }}
                      fontWeight="bold"
                    />
                  </Stack>
                }
                acceptType="image/*"
              />
            </Button>
            <Divider />

            <Button>
              <FilePicker
                handleFilePicked={(file) => {
                  console.log(file);
                  if (!file) return;
                  viewModel.setQuestionOrAnswerAttachment(
                    file,
                    AttachmentType.VIDEO,
                  );
                  handleClose();
                }}
                layout={
                  <Stack direction={"row"} p={2} spacing={2}>
                    <VideoFileOutlined sx={{ color: COLOR_LESS_BLACK }} />
                    <CustomTextView
                      text="Video"
                      props={{ textTransform: "none" }}
                      fontWeight="bold"
                    />
                  </Stack>
                }
                acceptType="video/*,.mp4,.mkv"
              />
            </Button>

            <Divider />
            <Button>
              <FilePicker
                handleFilePicked={(file) => {
                  console.log(file);
                  if (!file) return;
                  viewModel.setQuestionOrAnswerAttachment(
                    file,
                    AttachmentType.AUDIO,
                  );
                  handleClose();
                }}
                layout={
                  <Stack direction={"row"} p={2} spacing={2}>
                    <AudioFileOutlined sx={{ color: COLOR_LESS_BLACK }} />
                    <CustomTextView
                      text="Audio"
                      props={{ textTransform: "none" }}
                      fontWeight="bold"
                    />
                  </Stack>
                }
                acceptType="audio/*,.m4a,.mp3,.ogg,.wav"
              />
            </Button>
          </Stack>
        }
        anchorEl={anchorEl}
        onClose={handleClose}
      />
      {viewModel.isLoading ? (
        <LoadingView />
      ) : (
        <Stack>
          <Stack>
            <HeaderView
              isLayoutFullWidthInSmallerScreens
              title={STRINGS_QUESTION}
              titleLayout={
                <>
                  {viewModel.question?._id && (
                    <Marquee style={{ width: 100 }} delay={2} pauseOnHover>
                      <CustomTextView
                        text={`#${viewModel.question._id}}`}
                        fontWeight="300"
                        variant="h6"
                        textColor={COLOR_LIGHT_GRAY}
                      />
                    </Marquee>
                  )}
                </>
              }
              layout={
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={width < 900 ? "center" : "end"}
                  spacing={1}
                  width={"100%"}
                >
                  {!isNewPage && (
                    <Stack direction={"row"} alignItems={"center"} spacing={1}>
                      <Button
                        onClick={() => setShowDeleteAlertDialog(true)}
                        variant="outlined"
                        sx={{
                          borderRadius: 22,
                          borderColor: "red",
                          "&:hover": {
                            borderColor: "red",
                          },
                        }}
                      >
                        <Delete fontSize="small" sx={{ color: "red" }} />
                        <CustomTextView
                          text={STRINGS_DELETE}
                          fontWeight="bold"
                          textColor={"red"}
                          fontSize={14}
                          props={{ ml: 1, textTransform: "none" }}
                        />
                      </Button>
                    </Stack>
                  )}
                  <Button
                    onClick={() => {
                      isNewPage
                        ? viewModel.createQuestion()
                        : viewModel.saveQuestion();
                    }}
                    variant="outlined"
                    sx={{
                      borderRadius: 22,
                      borderColor: COLOR_LESS_BLACK,
                      "&:hover": {
                        borderColor: COLOR_LESS_BLACK,
                      },
                    }}
                  >
                    <Save fontSize="small" sx={{ color: COLOR_LESS_BLACK }} />
                    <CustomTextView
                      text={isNewPage ? STRINGS_CREATE : STRINGS_SAVE}
                      fontWeight="bold"
                      fontSize={14}
                      props={{ ml: 1, textTransform: "none" }}
                    />
                  </Button>
                </Stack>
              }
            />
            <Grid container spacing={2} p={3}>
              <Grid size={12}>
                <Masonry
                  columns={{ xs: 1, sm: 1, md: 1, lg: 2 }}
                  spacing={0}
                  sx={{ m: 0, p: 0 }}
                >
                  <Box
                    paddingRight={width < 1200 ? 0 : 1}
                    paddingBottom={width < 1200 ? 1 : 0}
                  >
                    <AutoHeight>
                      <CustomTitledView
                        height={rightColumnHeight}
                        hasDivider={false}
                        maxHeight={600}
                        title={STRINGS_ATTACHMENT}
                        onEditButtonClicked={(e) => handleClick(e, true)}
                        editIcon={
                          <Attachment
                            fontSize="medium"
                            sx={{ color: COLOR_LESS_BLACK }}
                          />
                        }
                        customIcon={
                          !isNewPage && viewModel.question?.attachment ? (
                            <IconButton
                              onClick={() => {
                                viewModel.setToDeleteQuestionAttachment(true);
                                setShowDeleteAttachmentAlertDialog(true);
                              }}
                            >
                              <DeleteRounded
                                fontSize="medium"
                                sx={{ color: COLOR_RED }}
                              />
                            </IconButton>
                          ) : undefined
                        }
                        layout={
                          <>
                            {viewModel.question?.attachment ? (
                              <AttachmentView
                                attachment={viewModel.question.attachment}
                              />
                            ) : (
                              <FileUploadingPlaceholder
                                onClick={(e) => handleClick(e, true)}
                              />
                            )}
                          </>
                        }
                      />
                    </AutoHeight>
                  </Box>
                  <Box
                    paddingLeft={width < 1200 ? 0 : 1}
                    paddingTop={width < 1200 ? 1 : 0}
                  >
                    <AutoHeight>
                      <Stack ref={ref} spacing={2}>
                        <CustomTitledView
                          title={STRINGS_QUESTION}
                          editIcon={
                            questionTitleEditable ? (
                              <CheckCircle
                                fontSize="medium"
                                sx={{ color: COLOR_LESS_BLACK }}
                              />
                            ) : (
                              <EditRounded
                                fontSize="medium"
                                sx={{ color: COLOR_LESS_BLACK }}
                              />
                            )
                          }
                          onEditButtonClicked={() => {
                            if (
                              viewModel.question?.title &&
                              viewModel.question.title.length === 0
                            )
                              return;
                            setQuestionTitleEditable(!questionTitleEditable);
                          }}
                          layout={
                            <div
                              onDoubleClick={() =>
                                viewModel.setTitleForQuestion("")
                              }
                            >
                              {questionTitleEditable ? (
                                <CustomTextField
                                  value={viewModel.question?.title}
                                  fontSize={24}
                                  textColor={COLOR_LESS_BLACK}
                                  multiLine
                                  fontWeight="bold"
                                  placeholder={STRINGS_EDIT_TITLE}
                                  variant="standard"
                                  onTextChange={viewModel.setTitleForQuestion}
                                  sxProps={{
                                    p: 3,
                                    direction: "rtl",
                                  }}
                                />
                              ) : (
                                <CustomTextView
                                  text={`${viewModel.question?.title
                                    ?.replaceAll("?", "")
                                    .replaceAll("؟", "")}؟`}
                                  fontWeight="bold"
                                  fontSize={24}
                                  props={{
                                    p: 3,
                                    direction: "rtl",
                                  }}
                                />
                              )}
                            </div>
                          }
                        />

                        <Stack
                          spacing={2}
                          direction={width < 1200 ? "column" : "row"}
                          width={"100%"}
                        >
                          <CustomTitledView
                            title={STRINGS_CATEGORY}
                            layout={
                              <>
                                {viewModel.categories &&
                                  viewModel.categories.length > 0 &&
                                  viewModel.selectedCategoryId && (
                                    <Select
                                      sx={{
                                        fontFamily: MAIN_FONT_FAMILY,
                                        borderRadius: 12,
                                        m: 2,
                                      }}
                                      inputProps={{
                                        MenuProps: {
                                          PaperProps: {
                                            sx: {
                                              borderRadius: 4,
                                            },
                                          },
                                        },
                                      }}
                                      value={
                                        viewModel.getCategoryById(
                                          viewModel.selectedCategoryId,
                                        )?.title
                                      }
                                      onChange={(
                                        e: SelectChangeEvent<string>,
                                      ) => {
                                        const id = viewModel.getCategoryByTitle(
                                          e.target.value,
                                        )?._id;
                                        if (!id) return;
                                        viewModel.setSelectedCategoryId(id);
                                      }}
                                    >
                                      {Object.values(viewModel.categories).map(
                                        (category, index) => {
                                          return (
                                            <MenuItem
                                              key={category._id}
                                              sx={{
                                                fontFamily: MAIN_FONT_FAMILY,
                                                direction: hasEnglishLetters(
                                                  category.title ?? "",
                                                )
                                                  ? "ltr"
                                                  : "rtl",
                                              }}
                                              value={category.title}
                                              divider={
                                                index !=
                                                (viewModel.categories?.length ??
                                                  0) -
                                                  1
                                              }
                                            >
                                              <Stack
                                                direction={"row"}
                                                alignItems={"center"}
                                                sx={{
                                                  direction: hasEnglishLetters(
                                                    category.title ?? "",
                                                  )
                                                    ? "ltr"
                                                    : "rtl",
                                                }}
                                                justifyContent={"space-between"}
                                                width={"100%"}
                                              >
                                                <CustomTextView
                                                  text={category.title}
                                                />
                                                <CustomImageView
                                                  width={40}
                                                  height={30}
                                                  scale="cover"
                                                  src={category.imageUrl}
                                                />
                                              </Stack>
                                            </MenuItem>
                                          );
                                        },
                                      )}
                                    </Select>
                                  )}
                              </>
                            }
                          />
                          <CustomTitledView
                            title={STRINGS_DIFFICULTY}
                            layout={
                              <Select
                                sx={{
                                  fontFamily: MAIN_FONT_FAMILY,
                                  borderRadius: 12,
                                  m: 2,
                                }}
                                inputProps={{
                                  MenuProps: {
                                    PaperProps: {
                                      sx: {
                                        borderRadius: 4,
                                      },
                                    },
                                  },
                                }}
                                value={viewModel.question?.difficulty}
                                onChange={(e) => {
                                  viewModel.setDifficulty(e.target.value);
                                }}
                              >
                                {Object.values(Difficulty).map((diff) => {
                                  return (
                                    <MenuItem
                                      key={diff}
                                      sx={{ fontFamily: MAIN_FONT_FAMILY }}
                                      value={diff}
                                    >
                                      {diff}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            }
                          />
                        </Stack>
                      </Stack>
                    </AutoHeight>
                  </Box>
                </Masonry>
              </Grid>
            </Grid>
          </Stack>
          <Stack>
            <Divider />
            <Grid container spacing={2} p={3}>
              <Grid size={12}>
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  display={"flex"}
                  alignItems={"center"}
                >
                  <Stack direction={"row"} alignItems={"center"} spacing={1}>
                    <TitleView title={STRINGS_ANSWER} />
                    {viewModel.answer?._id && (
                      <Marquee style={{ width: 100 }} delay={2} pauseOnHover>
                        <CustomTextView
                          text={`#${viewModel.answer._id}`}
                          fontWeight="300"
                          variant="h6"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                      </Marquee>
                    )}
                  </Stack>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, lg: 6, xl: 8 }}>
                <AutoHeight>
                  <CustomTitledView
                    title={STRINGS_ANSWER}
                    editIcon={
                      answerTitleEditable ? (
                        <CheckCircle
                          fontSize="medium"
                          sx={{ color: COLOR_LESS_BLACK }}
                        />
                      ) : (
                        <EditRounded
                          fontSize="medium"
                          sx={{ color: COLOR_LESS_BLACK }}
                        />
                      )
                    }
                    onEditButtonClicked={() => {
                      if (
                        viewModel.answer?.title &&
                        viewModel.answer.title.length === 0
                      )
                        return;
                      setAnswerTitleEditable(!answerTitleEditable);
                    }}
                    layout={
                      <Stack>
                        {answerTitleEditable ? (
                          <CustomTextField
                            value={viewModel.answer?.title}
                            multiLine
                            fontSize={24}
                            textColor={COLOR_LESS_BLACK}
                            fontWeight="bold"
                            placeholder={STRINGS_EDIT_TITLE}
                            variant="standard"
                            onTextChange={(txt) => {
                              viewModel.setTitleForAnswer(txt);
                            }}
                            sxProps={{
                              p: 3,
                              direction: "rtl",
                            }}
                          />
                        ) : (
                          <CustomTextView
                            text={viewModel.answer?.title}
                            fontWeight="bold"
                            fontSize={24}
                            props={{
                              p: 3,
                              direction: "rtl",
                            }}
                          />
                        )}
                      </Stack>
                    }
                  />
                </AutoHeight>
              </Grid>
              <Grid size={{ xs: 12, lg: 6, xl: 4 }}>
                <CustomTitledView
                  title={STRINGS_ATTACHMENT}
                  height={height * 0.45}
                  hasDivider={false}
                  onEditButtonClicked={(e) => handleClick(e, false)}
                  editIcon={
                    <Attachment
                      fontSize="medium"
                      sx={{ color: COLOR_LESS_BLACK }}
                    />
                  }
                  customIcon={
                    !isNewPage && viewModel.answer?.attachment ? (
                      <IconButton
                        onClick={() => {
                          viewModel.setToDeleteQuestionAttachment(false);
                          setShowDeleteAttachmentAlertDialog(true);
                        }}
                      >
                        <DeleteRounded
                          fontSize="medium"
                          sx={{ color: COLOR_RED }}
                        />
                      </IconButton>
                    ) : undefined
                  }
                  layout={
                    <>
                      {viewModel.answer?.attachment ? (
                        <>
                          <AttachmentView
                            attachment={viewModel.answer?.attachment}
                          />
                        </>
                      ) : (
                        <FileUploadingPlaceholder
                          onClick={(e) => handleClick(e, false)}
                        />
                      )}
                    </>
                  }
                />
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

interface a {
  attachment: AttachmentModel;
  imageScale?: string;
}
export function AttachmentView(a: a) {
  const renderVideo = useMemo(
    () => (
      <ReactPlayer
        width={"100%"}
        // height={"100%"}
        style={{ background: COLOR_LESS_BLACK }}
        controls={true}
        src={
          a.attachment.file
            ? URL.createObjectURL(a.attachment.file)
            : a.attachment.downloadUrl
        }
      />
    ),
    [a.attachment.downloadUrl, a.attachment.file],
  );
  return (
    <>
      {a.attachment.type === AttachmentType.IMAGE && (
        <CustomImageView
          width="100%"
          height="100%"
          scale={a.imageScale ?? "contain"}
          src={
            a.attachment.file
              ? URL.createObjectURL(a.attachment.file)
              : a.attachment.downloadUrl
          }
          props={{
            borderRadius: 8,
            background: GRADIENT_MAIN,
          }}
        />
      )}

      {a.attachment.type === AttachmentType.VIDEO && renderVideo}

      {a.attachment.type === AttachmentType.AUDIO && (
        <Stack height={"100%"} width={"100%"}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
            }}
          >
            <AudioFileRounded
              sx={{
                color: COLOR_LESS_BLACK,
                width: 100,
                height: 100,
              }}
            />
          </Box>
          <ReactAudioPlayer
            style={{ width: "100%", height: "20%" }}
            src={
              a.attachment.file
                ? URL.createObjectURL(a.attachment.file)
                : a.attachment.downloadUrl
            }
            controls
          />
        </Stack>
      )}
    </>
  );
}

interface p {
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}
export function FileUploadingPlaceholder(p: p) {
  return (
    <Button
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={p.onClick}
    >
      <CloudUploadRounded
        sx={{
          color: COLOR_BLACK,
          width: 200,
          height: 200,
        }}
      />
    </Button>
  );
}
